import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Typography } from '@telavita-core/react-design-kit'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'
import { SwitchingConfirmModal } from 'containers/ModalManager/Modals/SwitchingConfirmModal'

import MedicalRecordsService from 'services/medicalRecords'

import {
  MedicalRecordForm,
  MedicalRecordFormType,
} from 'models/MedicalRecord/MedicalRecordForm.type'

import { useDebounce } from 'hooks/useDebounce'

import { objectDeepCompare } from 'utils/objectDeepCompare'

import {
  MedicalRecordFormData,
  createMedicalRecordSchema,
} from './medicalRecordSchema'

import { MedicalRecordFormTypeSelect } from './components/MedicalRecordFormTypeSelect'
import { AutoSavingFeedback } from './components/AutoSavingFeedback'
import { SuccessModal } from './components/SuccessModal'
import { ErrorModal } from './components/ErrorModal'
import { LoadingSpinner } from './components/LoadingSpinner'

import {
  EVOLUTION_FORM,
  OUTCOME_FORM,
  TECHNICAL_EVALUATION_FORM,
  TypeForm,
  initialTypeFormOptions,
} from './types/forms'

import { useMedicalRecordForm } from './hooks/useMedicalRecordForm'

import { medicalRecordFormatData, medicalRecordFormFormatResponse } from './formatter'

import * as S from './styles'

interface MedicalRecordModalProps {
  onClose: () => void
  formUuid: string
  patientName: string
  planName: string
  scheduleCount: number
  onSuccess?: () => void
}

const FORM_TYPE_INPUT_NAME = 'type'

export function MedicalRecordModal({
  onClose,
  formUuid,
  patientName,
  planName,
  scheduleCount,
  onSuccess
}: MedicalRecordModalProps): JSX.Element {
  const { setForm, form, isSavingDraft, onUpdateDraft, isErrorSavingDraft } = useMedicalRecordForm()

  const formDraftDebouncedRef = useRef(null)

  const [showSwitchingConfirmModal, setShowSwitchingConfirmModal] = useState(
    false
  )
  const [selectedTypeForm, setSelectedTypeForm] = useState<TypeForm>(
    () => initialTypeFormOptions[0]
  )
  const [
    nextSelectedTypeForm,
    setNextSelectedTypeForm,
  ] = useState<TypeForm | null>(null)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [getFormIsLoading, setGetFormIsLoading] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [isVisibleModalError, setIsVisibleModalError] = useState(false)
  const [typeFormOptions, setTypeFormOptions] = useState<TypeForm[]>([])
  const [currentMedicalRecordForm, setCurrentMedicalRecordForm] = useState<
    MedicalRecordForm
  >()

  const formMethods = useForm<MedicalRecordFormData>({
    mode: 'onSubmit',
    resolver: yupResolver(createMedicalRecordSchema(selectedTypeForm.id)),
  })

  const { setValue, reset, watch } = formMethods

  const formDraft = watch()

  const formDraftDebounced = useDebounce(formDraft, 1000)

  const onSubmit = async (data: MedicalRecordFormData) => {
    try {
      setSubmitLoading(true)
      const medicalRecordFormatted = medicalRecordFormatData(data, form)
      await MedicalRecordsService.updateForm(formUuid, medicalRecordFormatted)
      onSuccess && onSuccess()
      setShowSuccessModal(true)
    } catch (error) {
      setIsVisibleModalError(true)
      console.error(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  function handleConfirmTypeFormChange() {
    reset(() => {
      const newForm = {
        type: nextSelectedTypeForm.id,
      } as MedicalRecordFormData
      
      onUpdateDraft({
        formData: newForm
      })
      return newForm
    })
    setSelectedTypeForm(nextSelectedTypeForm)
    setShowSwitchingConfirmModal(false)
  }

  function handleCancelTypeFormChange() {
    setNextSelectedTypeForm(null)
    setShowSwitchingConfirmModal(false)
  }

  function handleSelectedTypeFormChange(formId: string) {
    const newSelectedTypeForm = typeFormOptions.find(
      (form) => form.id === formId
    )

    if (!newSelectedTypeForm) return

    setNextSelectedTypeForm(newSelectedTypeForm)
    setShowSwitchingConfirmModal(true)
  }

  function handleCloseSuccessModal() {
    onClose()
  }

  function handleCloseErrorModal() {
    setIsVisibleModalError(false)
  }

  async function handleUpdateDraft() {
    setSubmitLoading(true)

    await onUpdateDraft({
      formData: formDraft
    })

    setSubmitLoading(false)
    onClose()
  }

  useEffect(() => {
    const getMedicalRecordForm = async () => {
      try {
        setGetFormIsLoading(true)
        const medicalRecordFormData = await MedicalRecordsService.getForm(formUuid)
        setCurrentMedicalRecordForm(medicalRecordFormData)
        setForm(medicalRecordFormData)
        const formattedMedicalRecordForm = medicalRecordFormFormatResponse(medicalRecordFormData)
        reset(formattedMedicalRecordForm, { keepDefaultValues: false })

        const isTechnicalEvaluation =
          medicalRecordFormData.type ===
          MedicalRecordFormType.TECHNICAL_EVALUATION

        const currentTypeFormOptions = isTechnicalEvaluation
          ? initialTypeFormOptions
          : initialTypeFormOptions.filter(
            (typeForm) =>
              typeForm.id !== MedicalRecordFormType.TECHNICAL_EVALUATION
          )

        const selectedTypeFormOption = initialTypeFormOptions.find(type => type.id === medicalRecordFormData.type)

        setSelectedTypeForm(selectedTypeFormOption)
        setTypeFormOptions(currentTypeFormOptions)
        setValue(FORM_TYPE_INPUT_NAME, selectedTypeFormOption.id)
      } catch (error) {
        console.error('Error fetching medical record form:', error)
      } finally {
        setGetFormIsLoading(false)
      }
    }
    getMedicalRecordForm()
  }, [formUuid])

  useEffect(() => {
    if (!(form?.uuid && form?.type)) return

    const hasValuesInForm = Object.values(formDraft).some(v => v)

    if (!hasValuesInForm) return

    const formIsEqual = objectDeepCompare(formDraft, formDraftDebounced)

    if (!formIsEqual) {
      const formDraftIsEqual = objectDeepCompare(formDraftDebouncedRef.current, formDraftDebounced)

      if (!formDraftIsEqual) {
        formDraftDebouncedRef.current = formDraftDebounced

        onUpdateDraft({
          formData: formDraft
        })
      }
    }
  }, [formDraft, formDraftDebounced, form])

  const typeFormIsTechnicalEvaluation = useMemo(
    () =>
      currentMedicalRecordForm?.type ===
      MedicalRecordFormType.TECHNICAL_EVALUATION,
    [currentMedicalRecordForm]
  )

  return (
    <>
      {showSuccessModal ? (
        <SuccessModal
          patientName={patientName}
          planName={planName}
          scheduleCount={scheduleCount}
          onClose={handleCloseSuccessModal}
        />
      ) : (
        <ModalGeneric
          modalTitle='Preencher prontuário'
          type='medium'
          onCloseModal={handleUpdateDraft}
          btnOnClick={onClose}
          floatingFeedback={(isSavingDraft || isErrorSavingDraft) && (
            <AutoSavingFeedback
              text='Salvamento automático'
              hasError={isErrorSavingDraft}
            />
          )}
        >
          <S.MedicalRecordContainer>
            <S.Header>
              <Typography variant='content2' center>
                {patientName}
              </Typography>
              {scheduleCount && (
                <Typography variant='content2' weight='bold' center>
                  {scheduleCount}ª consulta realizada
                </Typography>
              )}
              <Typography variant='content2' center>
                {planName}
              </Typography>
            </S.Header>

            <S.BoxInfo>
              <li>
                <div>
                  <Typography variant='content3'>
                    O sinalizador vermelho de pendência só deixará de ser
                    exibido quando todos os campos obrigatórios forem
                    preenchidos e você clicar em{' '}
                    <strong>“concluir e publicar no prontuário”</strong>.
                  </Typography>
                </div>
              </li>
            </S.BoxInfo>
            {getFormIsLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                <FormProvider {...formMethods}>
                  <S.Form>
                    <MedicalRecordFormTypeSelect
                      options={typeFormOptions}
                      disabled={typeFormIsTechnicalEvaluation}
                      selectedTypeForm={selectedTypeForm}
                      onSelectedTypeFormChange={handleSelectedTypeFormChange}
                    />
                    {selectedTypeForm.id === TECHNICAL_EVALUATION_FORM.id &&
                      TECHNICAL_EVALUATION_FORM.fields.map((field) => field)}
                    {selectedTypeForm.id === EVOLUTION_FORM.id &&
                      EVOLUTION_FORM.fields.map((field) => field)}
                    {selectedTypeForm.id === OUTCOME_FORM.id &&
                      OUTCOME_FORM.fields.map((field) => field)}
                  </S.Form>
                  <S.Footer>
                    <Typography variant='content2' italic>
                      * Preenchimento obrigatório
                    </Typography>

                    <S.ButtonsWrapper>
                      <Button
                        variant='contained'
                        uppercase={false}
                        onClick={formMethods.handleSubmit(onSubmit)}
                        disabled={submitLoading}
                      >
                        Concluir e publicar no prontuário
                      </Button>

                      <Button
                        variant='outlined'
                        uppercase={false}
                        onClick={handleUpdateDraft}
                        disabled={submitLoading}
                      >
                        Salvar e concluir depois
                      </Button>
                    </S.ButtonsWrapper>
                  </S.Footer>
                </FormProvider>
              </>
            )}
            {showSwitchingConfirmModal && (
              <SwitchingConfirmModal
                title='Ao mudar o tipo de formulário, você perderá as informações inseridas no campo anotações.'
                message='Se não quiser perder, volte e copie o que foi anotado. Ainda quer mudar o tipo de formulário?'
                onConfirmButtonText='Sim, mudar o tipo de formulário'
                onCancelButtonText='Cancelar'
                onClose={() => setShowSwitchingConfirmModal(false)}
                onConfirm={handleConfirmTypeFormChange}
                onCancel={handleCancelTypeFormChange}
              />
            )}
            {isVisibleModalError && (
              <ErrorModal
                patientName={patientName}
                planName={planName}
                scheduleCount={scheduleCount}
                onClose={handleCloseErrorModal}
              />
            )}
          </S.MedicalRecordContainer>
        </ModalGeneric>
      )}
    </>
  )
}
